import * as React from 'react';
import { useSelector } from 'react-redux';
import Popover from 'antd/lib/popover';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import type { RootState } from '../reducers/reducers';
import { selectAppSettings } from '../selectors/selectors';
import type { AssociatedAppStoreBuildInfo, CIBuildInfo } from '../types';
import { AppBuildComponent } from './BuildComponents';
import { antGrey } from './styles/globalStyles';

const MAX_VISIBLE_ASSOCIATED_BUILDS = 1;

interface CIBuildAssociatedAppStoreBuildsProps {
    appId: string;
    buildInfo: CIBuildInfo;
}

export const CIBuildAssociatedAppStoreBuilds: React.FC<CIBuildAssociatedAppStoreBuildsProps> = ({
    appId,
    buildInfo,
}) => {
    const destinationsByStepConfigId = useSelector(
        (state: RootState) => selectAppSettings(state, appId)?.destinationInfoByDestinationStepConfigId,
    );

    let associatedBuilds: AssociatedAppStoreBuildInfo[] = [];
    if (buildInfo.associatedAppStoreBuilds?.length) {
        associatedBuilds = buildInfo.associatedAppStoreBuilds;
    } else if (buildInfo.associatedAppStoreBuild) {
        associatedBuilds = [{ appStoreBuild: buildInfo.associatedAppStoreBuild, destinationStepConfigId: '' }];
    }

    if (associatedBuilds.length === 0) {
        return null;
    }

    const showDestinations = new Set(associatedBuilds.map((build) => build.destinationStepConfigId)).size > 1;

    const renderBuild = ({ appStoreBuild, destinationStepConfigId }: AssociatedAppStoreBuildInfo) => {
        const destination = showDestinations ? destinationsByStepConfigId?.[destinationStepConfigId] : undefined;
        return (
            <AppBuildComponent
                key={`${destinationStepConfigId}-${appStoreBuild.id}`}
                integrationId={appStoreBuild.integrationId}
                buildIdentifier={appStoreBuild.buildNumber}
                isProcessed={appStoreBuild.isProcessed}
                destination={destination}
            />
        );
    };

    const visibleBuilds = associatedBuilds.slice(0, MAX_VISIBLE_ASSOCIATED_BUILDS);
    const remainingBuilds = associatedBuilds.slice(MAX_VISIBLE_ASSOCIATED_BUILDS);

    return (
        <Space size={12} align="center">
            {visibleBuilds.map(renderBuild)}
            {remainingBuilds.length > 0 && (
                <Popover
                    content={
                        <Space direction="vertical" size={8}>
                            {remainingBuilds.map(renderBuild)}
                        </Space>
                    }
                    trigger="hover"
                    placement="bottom"
                >
                    <Text style={{ display: 'block', color: antGrey.grey6, fontSize: 12, cursor: 'pointer' }}>
                        +{remainingBuilds.length} more
                    </Text>
                </Popover>
            )}
        </Space>
    );
};
